import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import { ActivityIndicator, Text } from '@/components/ui';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { usePermissionMatrix } from '@/hooks/use-permission-matrix';
import type { PermissionCell } from '@/utils/permission-matrix';

/**
 * "Who can do what" — the permission model, on screen (#500).
 *
 * ONE renderer for the in-app view and the print route, per the project rule that print and
 * display share a renderer. A print page that re-implemented the table would drift from the
 * screen, and this document exists precisely so people can trust it.
 *
 * The data comes from `GET /permissions/matrix`, computed live by the worker. That is the
 * property that matters: this shows what the DEPLOYED API enforces, not what a checked-in
 * table claims. A stale description of the permission model is how #500 went unnoticed.
 */

/** Sideways scroll only where it is needed; a plain full-width block otherwise. */
const TableFrame: React.FC<{ horizontal: boolean; children: React.ReactNode }> = ({
	horizontal, children,
}) => (horizontal
	? (
		<ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={{ minWidth: 720 }}>
			{children}
		</ScrollView>
	)
	: <View style={{ width: '100%' }}>{children}</View>);

const MARK: Record<PermissionCell, { glyph: string; style: object }> = {
	role: { glyph: '✓', style: { color: '#2e7d32', fontWeight: '700' as const } },
	flag: { glyph: '●', style: { color: '#e65100', fontWeight: '700' as const } },
	never: { glyph: '✗', style: { color: '#c62828', fontWeight: '700' as const } },
	no: { glyph: '·', style: { color: '#b0bec5' } },
};

export const PermissionMatrixView: React.FC<{
	compact?: boolean;
	/** Hide the h1 when the container already shows the title — the modal does. */
	hideTitle?: boolean;
}> = ({ compact = false, hideTitle = false }) => {
	const { data, isLoading, error } = usePermissionMatrix();
	const isLargeScreen = useIsLargeScreen();

	if (isLoading) return <ActivityIndicator style={{ marginTop: 32 }} />;
	if (error || !data) {
		return (
			<Text style={styles.empty}>
				Could not load the permission list. {(error as Error | null)?.message ?? ''}
			</Text>
		);
	}

	const { roles, sections, flags, flagDelta, totalPermissions } = data;

	return (
		<ScrollView style={styles.scroll} contentContainerStyle={styles.page}>
			{!hideTitle && <Text style={styles.h1}>Who can do what</Text>}
			<Text style={styles.sub}>
				{totalPermissions} permissions · {roles.length} roles · {flags.length} per-user switches.
				Read live from the server, so it always matches what the app actually enforces.
			</Text>

			<View style={styles.callout}>
				<Text style={styles.calloutTitle}>Roles, then named capabilities</Text>
				<Text style={styles.calloutBody}>
					<Text style={styles.bold}>Role = Admin</Text> is a full super administrator and
					needs nothing else.{'\n'}
					<Text style={styles.bold}>Every other elevated power is a named capability</Text>,
					switched on per person — User Admin, Accounting, Inbox, SQL Admin and the rest.
					Each carries a badge, and relates to the table in one of two ways.{'\n'}
					<Text style={styles.capBadge}>UA</Text> <Text style={styles.bold}>on its own</Text> —
					the switch alone grants the permission, whatever the role. User Admin is the only
					one that grants table permissions: user administration and nothing else.{'\n'}
					<Text style={styles.capBadge}>AC</Text>{' '}
					<Text style={styles.bold}>on a permission the role already holds</Text> — the role
					has it, and the switch must also be on to use it. Accounting, Inbox and Tasks work
					this way: they open an area, they do not add rights. Each capability’s row below
					says which kind it is.{'\n'}
					<Text style={styles.bold}>Contractors</Text> are outside crew and can hold no
					capability at all — their column reads ✗ throughout.
				</Text>
			</View>

			<Text style={styles.h2}>Who sees what</Text>
			<Text style={styles.sub}>
				A tick says a role may do something, not where. The same tick means “every
				association” for one role and “only my own” for another, so each role’s reach is
				stated here, from the scope rules in the code.
			</Text>
			{roles.map((r) => (
				<View key={r.role} style={styles.flagRow}>
					<View style={styles.flagName}>
						<Text style={styles.permName}>{r.label}</Text>
						<Text style={styles.permId}>{r.role}</Text>
					</View>
					<View style={styles.flagBody}>
						<Text style={styles.flagGrants}>{r.reach}</Text>
					</View>
				</View>
			))}

			<Text style={styles.h2}>Capabilities</Text>
			<Text style={styles.sub}>
				Switched on per person, on the Users page. Admins pass every capability regardless of
				the switch.
			</Text>
			{(['access', 'elevated'] as const).map((group) => (
				<View key={group}>
					<Text style={styles.h3}>
						{group === 'access'
							? 'Feature access — opens an area, for roles that already hold its permissions'
							: 'Elevated capabilities — the switch itself grants the power'}
					</Text>
					{flags.filter((f) => f.group === group).map((f) => (
						<View key={f.id} style={styles.flagRow}>
							<View style={styles.flagName}>
								<Text style={styles.permName}>
									<Text style={styles.capBadge}>{f.badge}</Text>
									<Text> {f.label}</Text>
								</Text>
								<Text style={styles.permId}>{f.id}</Text>
							</View>
							<View style={styles.flagBody}>
								<Text style={styles.flagGrants}>{f.grants}</Text>
								<Text style={styles.flagKind}>
									{f.kind === 'unlocks'
										? 'Role AND switch: the role must already hold the permission; this opens it.'
										: 'Switch alone: grants the power to any eligible role.'}
								</Text>
								{f.floor ? (
									<Text style={styles.flagFloor}>
										Also requires the role permission {f.floor}.
									</Text>
								) : null}
								{f.note ? <Text style={styles.flagNote}>{f.note}</Text> : null}
							</View>
						</View>
					))}
				</View>
			))}

			<Text style={styles.h2}>Who can do what</Text>
			<View style={styles.legend}>
				<Text><Text style={MARK.role.style}>✓</Text> the role has it</Text>
				<Text><Text style={MARK.no.style}>·</Text> not granted</Text>
				<Text><Text style={MARK.never.style}>✗</Text> never for this role</Text>
			</View>

			{/*
				NO horizontal ScrollView on a wide screen.
				A horizontal ScrollView sizes its content to the content, not to the pane, so
				the table stopped ~150px short of the callouts above it no matter what flex the
				child asked for. On a large screen there is room for all seven columns, so the
				table is rendered directly and fills the width like every other block.
				On a phone it still scrolls sideways rather than crushing the tick columns into
				ambiguity, which is the one thing this table must never be.
			*/}
			<TableFrame horizontal={!isLargeScreen}>
				<View style={styles.table}>
					<View style={[styles.row, styles.headRow]}>
						<Text style={[styles.cellName, styles.headText]}>What the person can do</Text>
						{roles.map((r) => (
							<View key={r.role} style={styles.cell}>
								<Text style={styles.headText}>{r.label}</Text>
								{!compact && <Text style={styles.headBlurb}>{r.blurb}</Text>}
							</View>
						))}
					</View>

					{sections.map((sec) => (
						<View key={sec.title}>
							<View style={styles.sectionBand}>
								<Text style={styles.sectionTitle}>{sec.title}</Text>
								{!compact && <Text style={styles.sectionBlurb}>{sec.blurb}</Text>}
							</View>
							{sec.rows.map((r) => (
								<View key={r.id} style={styles.row}>
									<View style={styles.cellName}>
										<Text style={styles.permName}>{r.name}</Text>
										<Text style={styles.permId}>{r.id}</Text>
									</View>
									{r.cells.map((c, i) => (
										<View key={roles[i].role} style={styles.cell}>
											{c.capability ? (
												// Role AND switch keeps the tick; switch-alone is the badge by itself.
												<Text style={styles.capBadge}>
													{flags.find((f) => f.id === c.capability)?.badge ?? '?'}
												</Text>
											) : (
												<Text style={MARK[c.state].style}>{MARK[c.state].glyph}</Text>
											)}
										</View>
									))}
								</View>
							))}
						</View>
					))}
				</View>
			</TableFrame>

		</ScrollView>
	);
};

const styles = StyleSheet.create({
	// flex on the ScrollView, padding on the CONTENT — `flex: 1` on a content container
	// silently disables scrolling.
	scroll: { flex: 1 },
	page: { padding: 20, paddingBottom: 48, gap: 4 },
	h1: { fontSize: 22, fontWeight: '700', color: '#1a2027' },
	h2: { fontSize: 17, fontWeight: '700', color: '#1a2027', marginTop: 28 },
	h3: { fontSize: 14, fontWeight: '700', color: '#37474f', marginTop: 16, marginBottom: 4 },
	sub: { color: '#5d7986', fontSize: 12, marginBottom: 12 },
	bold: { fontWeight: '700' },
	callout: { borderLeftWidth: 4, borderLeftColor: '#1976d2', backgroundColor: '#f4f8fc', padding: 12, marginBottom: 10 },
	warn: { borderLeftColor: '#e65100', backgroundColor: '#fff8f0' },
	calloutTitle: { fontWeight: '700', marginBottom: 4, color: '#1a2027' },
	calloutBody: { color: '#37474f', fontSize: 12.5, lineHeight: 18 },
	tally: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
	tallyBox: { borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 6, padding: 8, minWidth: 110 },
	tallyLabel: { fontSize: 11, color: '#5d7986' },
	tallyNum: { fontSize: 18, fontWeight: '700', color: '#e65100' },
	legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginVertical: 10 },
	capBadge: {
		backgroundColor: '#e65100', color: '#fff', fontSize: 9, fontWeight: '700',
		paddingHorizontal: 4, paddingVertical: 1, borderRadius: 3, overflow: 'hidden',
	},
	table: { width: '100%', borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 6, overflow: 'hidden' },
	row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eceff1', alignItems: 'stretch' },
	headRow: { backgroundColor: '#eceff1' },
	headText: { fontWeight: '700', fontSize: 12, color: '#1a2027' },
	headBlurb: { fontSize: 9.5, color: '#5d7986' },
	// minWidth 0 so long permission names truncate instead of shoving the columns off-screen.
	cellName: { flex: 1, minWidth: 200, padding: 8, justifyContent: 'center' },
	cell: { width: 76, padding: 8, alignItems: 'center', justifyContent: 'center' },
	permName: { fontSize: 12.5, color: '#263238' },
	permId: { fontSize: 10, color: '#78909c', fontFamily: 'monospace' },
	sectionBand: { backgroundColor: '#37474f', paddingVertical: 6, paddingHorizontal: 8 },
	sectionTitle: { color: '#fff', fontWeight: '700', fontSize: 12.5 },
	sectionBlurb: { color: '#cfd8dc', fontSize: 10 },
	flagRow: { flexDirection: 'row', gap: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	flagName: { width: 170, minWidth: 0 },
	flagBody: { flex: 1, minWidth: 0 },
	flagGrants: { fontSize: 12.5, color: '#263238' },
	flagFloor: { fontSize: 11, color: '#e65100', marginTop: 2 },
	flagKind: { fontSize: 11, color: '#37474f', marginTop: 2, fontStyle: 'italic' },
	flagNote: { fontSize: 11, color: '#5d7986', marginTop: 2 },
	empty: { color: '#888', fontStyle: 'italic', padding: 32, textAlign: 'center' },
});
